import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Backdrop, HeroBackdrop } from '../../../modules/design-system';
import {
  getOrderIdFromURL,
  getViewFromURL,
  setGameIdInURL,
  setOrderIdInURL,
  setViewInURL,
} from '../../../modules/session';
import { useGamePurchase } from '../../../modules/game/LootBoxOpening';
import { RevealScreen } from '../../../modules/reveal';
import { useJustPulled } from '../../../modules/justPulled';
import { JustPulledPage } from '../../../pages/JustPulledPage';
import { MyPacksPage } from '../../../pages/MyPacks';
import { pushMyPacksRoute } from '../../../pages/MyPacks/myPacksRoute';
import type { ApiClient, AvailableCategory, IframePoolOrderItem } from '../../../modules/api';
import { Header, type HeaderCategory } from '../Header';
import { usePackData } from './usePackData';
import { useCategoryOffers } from './useCategoryOffers';
import { PackLayout } from './PackLayout';
import { PurchaseOverlay } from './PurchaseOverlay';

export interface PackPageProps {
  client: ApiClient;
  /** game_id from the URL, or null on first load. */
  initialGameId: string | null;
}

function toHeaderCategories(cats: AvailableCategory[]): HeaderCategory[] {
  return cats.map((c) => ({
    id: String(c.id),
    label: c.label,
    ...(c.iconSrc ? { iconSrc: c.iconSrc } : {}),
  }));
}

/**
 * Modern Pack page.
 *
 * Two-tier active state:
 *
 *   • `urlGameId`     — what's in the URL. Driven by header pill clicks;
 *                       resolves which sport's pack list is loaded.
 *   • `activeOfferId` — what's centred in the slider. Driven by swipes
 *                       too; the rest of the page (info card, odds, RIP
 *                       price) follows this value. URL is **not** updated
 *                       on swipe — the slider browses inside a category.
 *
 * Switching category in the header resets both: URL changes to the
 * category's representative pool_offer_id and the slider snaps to it.
 */
export function PackPage({
  client,
  initialGameId,
}: PackPageProps) {
  const [urlGameId, setUrlGameId] = useState<string | null>(initialGameId);
  const [activeOfferId, setActiveOfferId] = useState<string | null>(initialGameId);
  const [quantity, setQuantity] = useState(1);
  // Mirror of the URL's `?order_id=…` — when set, <RevealScreen /> owns
  // the viewport (mirrors classic). Refresh-safe: state is seeded from
  // the URL on mount, so a hard reload during ripping resumes there.
  const [revealOrderId, setRevealOrderId] = useState<string | null>(() =>
    getOrderIdFromURL(),
  );

  // Sub-views inside Pack: `'pack'` (default), `'just-pulled'` (full-page
  // list opened by the JustPulled chevron) and `'my-packs'` (header
  // button). Mirrors classic — both writes & reads from `?view=…` so
  // refresh keeps you on the same screen.
  const [view, setView] = useState<'pack' | 'just-pulled' | 'my-packs'>(() => {
    const fromUrl = getViewFromURL();
    if (fromUrl === 'my-packs') return 'my-packs';
    if (fromUrl === 'just-pulled') return 'just-pulled';
    return 'pack';
  });

  // Items handed from the reveal "Confirm Shipping" CTA into the My Packs
  // delivery flow. Seeds the create-shipping page; cleared when My Packs closes.
  const [deliverySeed, setDeliverySeed] = useState<IframePoolOrderItem[] | null>(null);

  useEffect(() => {
    if (revealOrderId === null) setOrderIdInURL(null);
  }, [revealOrderId]);

  // Once My Packs has mounted with the reveal ship-seed (it copies it into a
  // one-shot of its own at mount), drop our copy — otherwise a later My Packs
  // remount would re-capture the stale seed and pre-load a Cards-tab delivery
  // with the already-shipped card.
  useEffect(() => {
    if (deliverySeed && view === 'my-packs') setDeliverySeed(null);
  }, [deliverySeed, view]);

  useEffect(() => {
    setViewInURL(view === 'pack' ? null : view);
  }, [view]);

  // Detail / odds / hits / categories for the centred offer.
  const {
    offer,
    categories,
    odds,
    maxForPurchaseCount,
    bgImageUrl,
    potentialHits,
    potentialHitsLoading,
  } = usePackData(client, activeOfferId);

  // First-load fallback — seed both ids from the first available category
  // when the host didn't pass a `game_id`.
  useEffect(() => {
    if (urlGameId !== null) return;
    if (categories.length === 0) return;
    const first = categories[0];
    if (!first) return;
    const id = String(first.id);
    setUrlGameId(id);
    setActiveOfferId(id);
  }, [urlGameId, categories]);

  // The "active category" is the one whose representative pool_offer_id
  // matches the URL — that determines which sport pill is highlighted
  // and which offers populate the slider.
  const urlCategory = useMemo<AvailableCategory | null>(() => {
    if (urlGameId === null) return null;
    return categories.find((c) => String(c.id) === urlGameId) ?? null;
  }, [categories, urlGameId]);

  const { offers: categoryOffers } = useCategoryOffers(client, urlCategory);

  // Live "Just Pulled" feed scoped to the currently centred pack —
  // refetches whenever the user swipes to a new offer so the strip
  // always reflects the visible pack. `null` would give the global
  // (cross-pack) feed; we mirror classic's per-pack scope.
  const { items: justPulledItems, loading: justPulledLoading } =
    useJustPulled(client, { poolOfferId: activeOfferId ?? null });

  // Purchase orchestration — `useGamePurchase` owns the wallet refresh,
  // the buy→reveal flow, the API wrapper, and the Just-Pulled quick-buy
  // queue. The slider can swap the visible pack without touching the
  // URL, so we hand the hook `activeOfferId` (centred offer) with a
  // `urlGameId` fallback. Shared with the classic Pack page.
  const { buy, queueRip, retry, flow, resetFlow } = useGamePurchase({
    client,
    offerId: activeOfferId ?? urlGameId ?? '',
    offer,
    quantity,
    setQuantity,
    onRevealReady: (orderId) => {
      setOrderIdInURL(orderId);
      setRevealOrderId(orderId);
    },
  });

  // `activeOfferId` is owned by explicit user intent — header pill click
  // and slider swipe each set it directly. Don't auto-snap here on
  // `categoryOffers` change: during a category switch the offers list is
  // briefly stale (still old sport), and any snap-to-first-in-list logic
  // races with the freshly-set representative and overwrites it. The
  // Slider's own `externalIndex` falls back to 0 visually when the
  // active id isn't (yet) in the list — that's the correct intermediate
  // state until the new offers resolve.

  const headerCategories = toHeaderCategories(categories);
  const activeCategoryId =
    urlGameId !== null && headerCategories.some((c) => c.id === urlGameId)
      ? urlGameId
      : undefined;

  // Post-purchase reveal — when `?order_id=X` is present the reveal screen
  // owns the viewport (matches classic). Refresh-safe: state is mirrored
  // from the URL on mount, so a hard reload while ripping resumes here.
  let content: ReactNode = null;
  if (revealOrderId) {
    content = (
      <RevealScreen
        client={client}
        orderId={revealOrderId}
        onClose={() => setRevealOrderId(null)}
        onBuyAgain={() => buy()}
        onShowPack={() => setRevealOrderId(null)}
        onShip={(item) => {
          // Confirm Shipping → drop into My Packs' create-delivery flow,
          // seeded with this card. Open the route first so My Packs mounts
          // straight into delivery-new.
          setDeliverySeed([item]);
          pushMyPacksRoute({ kind: 'delivery-new' });
          setRevealOrderId(null);
          setView('my-packs');
        }}
      />
    );
  }

  return (
    <>
      {content}

      {/* Pack view scope — wraps Backdrop AND the content div so the
          `[data-page='pack']` CSS-var redefinition in index.css reaches
          the gradient layer. Backdrop was previously a sibling of the
          wrapper, which left it reading root-level `--ui-bg-gradient`
          and ignoring page-specific knobs.

          Hidden while reveal is active so the page underneath doesn't
          flash during refresh-safe restore. */}
      <div data-page="pack" className={revealOrderId ? 'hidden' : ''}>
        <Backdrop bgImageUrl={bgImageUrl} />
      </div>

      {!revealOrderId && view === 'just-pulled' && (
        <JustPulledPage
          client={client}
          packId={activeOfferId ?? null}
          bgImageUrl={bgImageUrl}
          onBack={() => setView('pack')}
          onRip={(offerId) => {
            // Quick-buy: switch to that offer's pack page; the hook
            // resets quantity to 1 and queues `purchase.run()` to fire
            // once the new offer finishes loading.
            const id = String(offerId);
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
            queueRip(offerId);
            setView('pack');
          }}
        />
      )}

      {!revealOrderId && view === 'my-packs' && (
        <MyPacksPage
          client={client}
          {...(deliverySeed ? { deliverySeed } : {})}
          onBack={() => {
            setDeliverySeed(null);
            setView('pack');
          }}
          onOpenPack={(unopenedOffer) => {
            // "Open Now" on a Repackz-tab row — switch the pack page to
            // that offer and drop into the reveal screen via URL.
            if (!unopenedOffer.firstUnopenedOrderId) return;
            // Don't slip a reveal in on top of an active purchase — the
            // PurchaseOverlay would land over a freshly-loaded reveal of
            // an unrelated order. No-op until the buy flow settles.
            if (flow.step !== 'idle') return;
            const id = String(unopenedOffer.id);
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
            setOrderIdInURL(unopenedOffer.firstUnopenedOrderId);
            setRevealOrderId(unopenedOffer.firstUnopenedOrderId);
            setView('pack');
          }}
          onBuyAnother={(offerId) => {
            // Drops-tab card-modal "Buy Another Repack" — focus the
            // slider on that pack and return to the Pack view.
            const id = String(offerId);
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
            setView('pack');
          }}
        />
      )}

      {/* Conditionally render — not just `hidden` — because the
          BottomAction `MobileBar` inside PackLayout portals to <body>
          and would leak through `display: none` (the portal escapes the
          parent's class). Unmounting on view-change drops the portal too. */}
      {!revealOrderId && view === 'pack' && (
      <div
        data-co-component="PackPageBackground"
        className="@container relative min-h-screen w-full"
        style={{ color: 'var(--color-text-on-brand)' }}
      >
        {/* Sonar-waves backdrop — spans header + slider area so the
            rings bleed *under* the navigation bar at the top and
            *past* the pedestal at the bottom, matching Figma 543:2118.
            Horizontally constrained on desktop to the slider column
            only (stops before the sidebar) so the wave's centre lines
            up with the centre pack — otherwise the rings would
            visually drift right toward viewport centre.
            Perf:
              • `scale(1.5)` instead of `scale(2)` — paint area is the
                square of scale, so 1.5² ≈ 56% of the work 2² did, with
                still enough bleed to fill the slot.
              • `[will-change:transform]` + `[transform:translateZ(0)]`
                lift the inner wrapper onto its own GPU compositing
                layer so Lottie's per-frame repaints don't reflow the
                surrounding page. */}
        <div
          aria-hidden
          className="
            pointer-events-none absolute left-0 right-0 top-0 -z-10 overflow-hidden
            h-[560px]
            min-[600px]:h-[640px]
            min-[900px]:right-[384px] min-[900px]:h-[700px]
            min-[1200px]:right-[424px]
            [contain:layout_paint]
          "
        >
          <div
            className="
              absolute inset-0 origin-center
              [transform:translateZ(0)_scale(1.5)]
              [will-change:transform]
              [backface-visibility:hidden]
            "
          >
            <HeroBackdrop
              animationUrl={offer?.heroAnimationUrl ?? null}
              renderer="canvas"
            />
          </div>
        </div>

        <Header
          categories={headerCategories}
          {...(activeCategoryId ? { activeCategoryId } : {})}
          onCategorySelect={(id) => {
            // Header pill — switch category. URL + slider both snap to
            // the new category's representative offer.
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          onMyPacksClick={() => setView('my-packs')}
        />

        <PackLayout
          offer={offer}
          categoryOffers={categoryOffers}
          activeOfferId={activeOfferId !== null ? Number(activeOfferId) : null}
          odds={odds}
          maxForPurchaseCount={maxForPurchaseCount}
          potentialHits={potentialHits}
          potentialHitsLoading={potentialHitsLoading}
          justPulledItems={justPulledItems}
          justPulledLoading={justPulledLoading}
          onShowJustPulled={() => setView('just-pulled')}
          categories={headerCategories}
          {...(activeCategoryId ? { activeCategoryId } : {})}
          onCategorySelect={(id) => {
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          onSlideChange={(offerId) => {
            // Slider swipe — update centred offer in state only, keep URL.
            setActiveOfferId(String(offerId));
          }}
          onTierSelect={(poolOfferId) => {
            const id = String(poolOfferId);
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          quantity={quantity}
          onQuantityChange={setQuantity}
          onBuy={buy}
          unitPrice={offer?.price ?? 0}
          operatorCurrency={offer?.operatorCurrency ?? null}
          exchangeRate={offer?.exchangeRate ?? null}
        />
      </div>
      )}

      {/* Global purchase overlay — shows the spinner during the API
          round-trip and the failure screen on errors. Success closes
          the overlay and hands off to <RevealScreen> via the URL. */}
      <PurchaseOverlay state={flow} onClose={resetFlow} onRetry={retry} />
    </>
  );
}
